/**
 * Consumer filter rail for /map — five facets in consumer language.
 *
 * Renders a "Filters" trigger pill (with active-count badge) and, when open,
 * a panel: floating card on desktop, bottom sheet on narrow screens. All
 * changes call onChange immediately (client-side filtering is instant);
 * filtered-out hospitals dim on the map rather than disappearing.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { SlidersHorizontal, X, RotateCcw } from "lucide-react";
import { gradeColor } from "@/lib/grades";
import { MapFilters, DEFAULT_FILTERS, countActiveFilters } from "@/lib/mapFilters";

interface Props {
  filters: MapFilters;
  onChange: (f: MapFilters) => void;
  visibleCount: number;
  totalCount: number;
  dark: boolean;
  isNarrow: boolean;
  /** Offset from the top of the map container for the trigger pill (px). */
  topOffset?: number;
  /** Desktop: distance from the container's right edge (clears the right panel). */
  rightOffset?: number;
}

export const MapFilterRail: React.FC<Props> = ({
  filters, onChange, visibleCount, totalCount, dark, isNarrow, topOffset = 12, rightOffset = 12,
}) => {
  const { t } = useTranslation("map");
  const [open, setOpen] = useState(false);
  const active = countActiveFilters(filters);

  const panelBg = dark ? "rgba(var(--civic-card-dark-rgb),0.97)" : "rgba(255,255,255,0.98)";
  const panelBorder = dark ? "rgba(var(--civic-gold-rgb),0.3)" : "rgba(var(--civic-navy-rgb),0.15)";
  const ink = dark ? "var(--civic-warm)" : "var(--civic-ink)";
  const muted = dark ? "rgba(var(--civic-warm-rgb),0.6)" : "rgba(var(--civic-ink-rgb),0.62)";
  const chipIdleBg = dark ? "rgba(255,255,255,0.06)" : "rgba(var(--civic-ink-rgb),0.05)";
  const chipIdleBorder = dark ? "rgba(255,255,255,0.12)" : "rgba(var(--civic-ink-rgb),0.12)";
  const accent = dark ? "var(--civic-gold)" : "var(--civic-navy)";

  const toggleIn = (arr: string[], v: string) =>
    arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  const sectionTitle = (label: string) => (
    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: muted, margin: "10px 0 5px" }}>
      {label}
    </div>
  );

  const chip = (selected: boolean, onClick: () => void, label: React.ReactNode, color?: string) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer",
        backgroundColor: selected ? (color ? color : accent) : chipIdleBg,
        color: selected ? "#fff" : ink,
        border: `1px solid ${selected ? (color || accent) : chipIdleBorder}`,
        transition: "all 0.12s",
      }}
    >
      {label}
    </button>
  );

  const panel = (
    <div
      style={{
        backgroundColor: panelBg, border: `1px solid ${panelBorder}`, color: ink,
        borderRadius: isNarrow ? "16px 16px 0 0" : 12, boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        backdropFilter: "blur(10px)", padding: "12px 14px",
        ...(isNarrow
          ? { position: "fixed" as const, left: 0, right: 0, bottom: 0, zIndex: 400, maxHeight: "70vh", overflowY: "auto" as const, paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }
          : { position: "absolute" as const, top: topOffset + 40, right: rightOffset, zIndex: 300, width: 272, maxHeight: "calc(100% - 120px)", overflowY: "auto" as const }),
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{t("filters.title", { defaultValue: "Filter hospitals" })}</span>
        <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {active > 0 && (
            <button
              type="button"
              onClick={() => onChange({ ...DEFAULT_FILTERS })}
              title={t("filters.reset", { defaultValue: "Reset" })}
              style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, color: muted, background: "none", border: "none", cursor: "pointer" }}
            >
              <RotateCcw style={{ width: 12, height: 12 }} /> {t("filters.reset", { defaultValue: "Reset" })}
            </button>
          )}
          <button type="button" onClick={() => setOpen(false)} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: muted }}>
            <X style={{ width: 15, height: 15 }} />
          </button>
        </span>
      </div>
      <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
        {t("filters.showing", { defaultValue: "Showing {{x}} of {{y}} hospitals", x: visibleCount.toLocaleString(), y: totalCount.toLocaleString() })}
      </div>

      {sectionTitle(t("filters.priceFairness", { defaultValue: "Price fairness" }))}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {(["A", "B", "C", "D", "F"] as const).map(g =>
          chip(filters.grades.includes(g), () => onChange({ ...filters, grades: toggleIn(filters.grades, g) }), g, gradeColor(g))
        )}
      </div>

      {sectionTitle(t("filters.careQuality", { defaultValue: "Care quality" }))}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {chip(filters.minStars == null, () => onChange({ ...filters, minStars: null }), t("filters.anyStars", { defaultValue: "Any rating" }))}
        {[3, 4, 5].map(s =>
          chip(filters.minStars === s, () => onChange({ ...filters, minStars: filters.minStars === s ? null : s }), `${s}★+`)
        )}
        {chip(filters.erOnly, () => onChange({ ...filters, erOnly: !filters.erOnly }), t("filters.hasEr", { defaultValue: "Has ER" }))}
      </div>

      {sectionTitle(t("filters.hospitalKind", { defaultValue: "Hospital kind" }))}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {([
          ["acute", t("filters.typeAcute", { defaultValue: "General" })],
          ["rural", t("filters.typeRural", { defaultValue: "Rural & critical access" })],
          ["children", t("filters.typeChildren", { defaultValue: "Children's" })],
          ["psych", t("filters.typePsych", { defaultValue: "Psychiatric" })],
        ] as const).map(([v, label]) =>
          chip(filters.types.includes(v), () => onChange({ ...filters, types: toggleIn(filters.types, v) }), label)
        )}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
        {([
          ["any", t("filters.ownAny", { defaultValue: "Any owner" })],
          ["nonprofit", t("filters.ownNonprofit", { defaultValue: "Nonprofit" })],
          ["forprofit", t("filters.ownForprofit", { defaultValue: "For-profit" })],
          ["government", t("filters.ownGovernment", { defaultValue: "Government" })],
        ] as const).map(([v, label]) =>
          chip(filters.ownership === v, () => onChange({ ...filters, ownership: v }), label)
        )}
      </div>

      {sectionTitle(t("filters.size", { defaultValue: "Hospital size" }))}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {([
          ["S", t("filters.sizeS", { defaultValue: "Small <100 beds" })],
          ["M", t("filters.sizeM", { defaultValue: "Medium 100–399" })],
          ["L", t("filters.sizeL", { defaultValue: "Large 400+" })],
        ] as const).map(([v, label]) =>
          chip(filters.bedSizes.includes(v), () => onChange({ ...filters, bedSizes: toggleIn(filters.bedSizes, v) }), label)
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Trigger pill */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          ...(isNarrow
            ? { position: "fixed" as const, bottom: "calc(150px + env(safe-area-inset-bottom, 0px))", right: 12, zIndex: 300 }
            : { position: "absolute" as const, top: topOffset, right: rightOffset, zIndex: 300 }),
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "7px 13px", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer",
          backgroundColor: active > 0 ? accent : panelBg,
          color: active > 0 ? "#fff" : ink,
          border: `1px solid ${active > 0 ? accent : panelBorder}`,
          boxShadow: "0 2px 10px rgba(0,0,0,0.25)", backdropFilter: "blur(8px)",
        }}
      >
        <SlidersHorizontal style={{ width: 13, height: 13 }} />
        {t("filters.trigger", { defaultValue: "Filters" })}
        {active > 0 && (
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            minWidth: 17, height: 17, borderRadius: 9, padding: "0 4px",
            backgroundColor: "#fff", color: accent, fontSize: 10, fontWeight: 800,
          }}>{active}</span>
        )}
      </button>
      {/* Count chip when filtering but panel closed */}
      {!open && active > 0 && (
        <div style={{
          ...(isNarrow
            ? { position: "fixed" as const, bottom: "calc(190px + env(safe-area-inset-bottom, 0px))", right: 12, zIndex: 290 }
            : { position: "absolute" as const, top: topOffset + 40, right: rightOffset, zIndex: 290 }),
          padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700,
          backgroundColor: panelBg, color: ink, border: `1px solid ${panelBorder}`,
        }}>
          {t("filters.showing", { defaultValue: "Showing {{x}} of {{y}} hospitals", x: visibleCount.toLocaleString(), y: totalCount.toLocaleString() })}
        </div>
      )}
      {open && panel}
    </>
  );
};
